import { QRCodeSVG } from "qrcode.react";
import type { SessionPanelist } from "../../shared/types.ts";

/** Static join targets (user decision 2026-07-14): the QR encodes the short
 *  scan link while the visible pill shows the typeable one — deliberately
 *  different so scans and typed visits are distinguishable. Neither derives
 *  from the session join code. */
export const JOIN_QR_URL = "https://stla.one/asr26";
export const JOIN_LINK_LABEL = "stla.one/comodo";

/** The roster must never push the stage into scrolling. */
const ROSTER_LIMIT = 8;

export type JoinStageContent =
  | { kind: "full"; title: string; panelists: SessionPanelist[] }
  | { kind: "title-only"; title: string }
  | { kind: "brand-only" };

export function deriveJoinStageContent(
  sessionTitle: string | null | undefined,
  panelists: SessionPanelist[],
): JoinStageContent {
  const title = sessionTitle?.trim() ?? "";
  const roster = panelists.filter((panelist) => panelist.name.trim()).slice(0, ROSTER_LIMIT);
  if (title && roster.length > 0) return { kind: "full", title, panelists: roster };
  if (title) return { kind: "title-only", title };
  return { kind: "brand-only" };
}

/** Full-page session splash for the projector's QR mode: brand + session
 *  title + roster on paper, the intro page's floating slate join card on the
 *  right. Renders without the global projector header (the title lives here). */
export function ProjectorJoinStage({
  sessionTitle,
  panelists,
}: {
  sessionTitle: string | null | undefined;
  panelists: SessionPanelist[];
}) {
  const content = deriveJoinStageContent(sessionTitle, panelists);
  return (
    <div className={`pjoin pjoin-${content.kind}`} data-testid="projector-join-stage">
      <div className="pjoin-left">
        <div className="pjoin-brand">
          <span className="pjoin-mark" aria-hidden="true" />
          <span className="pjoin-word">COMODO</span>
        </div>
        {content.kind !== "brand-only" && (
          <>
            <span className="pjoin-kick">Live panel</span>
            <h1 className="pjoin-title">{content.title}</h1>
          </>
        )}
        {content.kind === "full" && (
          <div className="pjoin-speakers" data-testid="projector-join-roster">
            {content.panelists.map((panelist) => (
              <div className="pjoin-speaker" key={panelist.name}>
                <span className="pjoin-speaker-name">
                  {panelist.name}
                  {panelist.isModerator && <span className="pjoin-modtag">Moderator</span>}
                </span>
                {panelist.bio.trim() && <span className="pjoin-speaker-bio">{panelist.bio}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="pjoin-right">
        <div className="pjoin-qr-tile" data-testid="projector-join-stage-qr">
          <QRCodeSVG value={JOIN_QR_URL} size={512} marginSize={0} bgColor="transparent" fgColor="#253048" />
        </div>
        <span className="pjoin-link">{JOIN_LINK_LABEL}</span>
      </div>
    </div>
  );
}
